import { useState, useEffect, useMemo } from 'react';

import { useMutation } from '@tanstack/react-query';
import dynamic from 'next/dynamic';

import { RadioGroup } from '@components/RadioGroup';
import Counter from '@components/Counter/Counter';
import {} from '@api/index';
import { useBasketId } from '@hooks/cache';
import {
  convertStringDateFormat,
  DEFAULT_DATE_FORMAT,
  URL_DATE_FORMAT,
} from '@utils/dateUtil';
import { InfoNote } from '@components/views/payment/components/common/styled';
import { callService } from '@api/remote-call';
import { useDevice } from '@contexts/device';
import {
  HotelTransferSearchViewModel,
  TransferType,
  TransferVehicleType,
} from '@api/v3';
import Typography from '@components/Typography';

import { Content, FooterMobile } from '../styled';
import {
  GuestCount,
  FormContainer,
  CounterWrapper,
  TransferContainer,
  RadioGroupContainer,
  StyledButton,
  Footer,
  DateText,
} from './styled';
import TransferItemForm from './TransferItemForm';

const Modal = dynamic(() => import('@components/Modal/next/Modal'));

type FlightTripOption = { value: TransferType; label: string };

const flightTripOptions: FlightTripOption[] = [
  { value: TransferType.ROUND_TRIP, label: 'Gidiş - Dönüş' },
  { value: TransferType.DEPARTURE, label: 'Sadece Gidiş' },
  { value: TransferType.RETURN, label: 'Sadece Dönüş' },
];

const getLabel = (option: FlightTripOption) => option.label;
const getValue = (option: FlightTripOption) => option.value;

export type TransferServiceInitialFormType = {
  hotelId?: number;
  regionId?: number;
  startPointId?: number;
  beginDate?: string;
  endDate?: string;
  basketParentId: string;
  itemId: string;
  guestCount: number;
  transferData: HotelTransferSearchViewModel[];
};

type TransferServiceFormProps = {
  onCancel?(): void;
  onSuccess?(): void;
  initialValues: TransferServiceInitialFormType;
};

const TransferServiceForm = ({
  onCancel,
  onSuccess,
  initialValues,
}: TransferServiceFormProps) => {
  const [count, setCount] = useState(1);
  const [vehicleType, setVehicleType] = useState<TransferVehicleType>();
  const [tripType, setTripType] = useState<TransferType>(
    flightTripOptions[0].value
  );
  const [selectedTransfer, setSelectedTransfer] = useState<number>();
  const { isDesktop } = useDevice();
  const basketId = useBasketId();

  const transferServiceData = initialValues?.transferData;

  const addTransfer = async () => {
    if (basketId && selectedTransfer) {
      const voucherDeparture = {
        id: selectedTransfer,
        guestCount: count,
        vehicleType,
        transferDate: initialValues.beginDate,
      };
      const voucherReturn = {
        id: selectedTransfer,
        guestCount: count,
        vehicleType,
        transferDate: initialValues.endDate,
      };
      {
        transferServiceData?.map((item) => {
          if (selectedTransfer === item.id) {
            voucherDeparture.vehicleType = item.vehicleType;
            setVehicleType(item.vehicleType);
          }
        });
      }

      const voucherList =
        tripType === TransferType.ROUND_TRIP
          ? [voucherDeparture, voucherReturn]
          : tripType === TransferType.DEPARTURE
          ? [voucherDeparture]
          : [voucherReturn];

      const body = {
        prebookParameters: {
          promotions: [],
          voucherList,
          type: tripType,
          basketParentId: initialValues.itemId,
        },
      };

      return callService(
        {
          service: 'PrebookService',
          method: 'prebookAddTransfer',
          version: 'v3',
        },
        {
          basketId,
          body,
        }
      );
    }
  };

  const { mutate, isLoading: isSubmittingForm } = useMutation(
    ['prebook-transfer-service'],
    addTransfer,
    {
      onSuccess: () => {
        onSuccess?.();
      },
    }
  );

  useEffect(() => {
    transferServiceData?.length &&
      setSelectedTransfer(transferServiceData[0].id);
  }, [transferServiceData]);

  const onChangeTripType = (tripType: TransferType) => {
    setTripType(tripType);
  };

  const onChangeTransfer = (transfer: number) => {
    setSelectedTransfer(Number(transfer));
  };

  const transferDateInfoText = useMemo(() => {
    if (tripType && initialValues?.beginDate && initialValues?.endDate) {
      const beginDate = convertStringDateFormat(
        initialValues.beginDate,
        URL_DATE_FORMAT,
        DEFAULT_DATE_FORMAT
      );
      const endDate = convertStringDateFormat(
        initialValues.endDate,
        URL_DATE_FORMAT,
        DEFAULT_DATE_FORMAT
      );

      if (tripType === TransferType.DEPARTURE) {
        return beginDate;
      } else if (tripType === TransferType.RETURN) {
        return endDate;
      } else if (tripType === TransferType.ROUND_TRIP) {
        return beginDate + ' - ' + endDate;
      }
    }
    return '';
  }, [initialValues, tripType]);

  const hasValidTransferItem = transferServiceData
    ? transferServiceData.findIndex(
        (transfer) => !!transfer.vehicleMaxPersonCount
      ) >= 0
    : false;

  if (isDesktop === undefined) return null;

  return (
    <Modal
      borderRadius={8}
      title={
        <Typography variant='Title4' weight='SemiBold' color='D400'>
          Havalimanı Transfer Hizmeti Ekleyin
        </Typography>
      }
      backgroundColor='lynxWhite'
      onCancel={onCancel}
      width={!isDesktop ? '100%' : hasValidTransferItem ? 930 : 600}
      closable={!isSubmittingForm}
    >
      {hasValidTransferItem ? (
        <>
          <Content>
            <FormContainer>
              <RadioGroup
                options={flightTripOptions}
                selectedIndex={0}
                direction={isDesktop ? 'row' : 'column'}
                getLabel={getLabel}
                getValue={getValue}
                onChange={onChangeTripType}
                disabled={isSubmittingForm}
              />
              <GuestCount>
                <Typography variant='caption' weight='Bold' color='D400'>
                  Kaç Kişilik Transfer Hizmeti Almak İstiyorsunuz?
                </Typography>
                <CounterWrapper>
                  <Counter
                    count={count}
                    handleDecrement={() => setCount(count - 1)}
                    handleIncrement={() => setCount(count + 1)}
                    disabledDecrement={isSubmittingForm}
                    disabledIncrement={isSubmittingForm}
                    max={initialValues.guestCount}
                  />
                </CounterWrapper>
              </GuestCount>
              <Typography variant='Title4' weight='SemiBold' color='D400'>
                {flightTripOptions.find((item) => item.value === tripType)
                  ?.label + ' Havalimanı Transferi'}
              </Typography>
              <TransferContainer>
                <DateText>{transferDateInfoText}</DateText>
                <RadioGroupContainer>
                  <RadioGroup
                    options={transferServiceData}
                    selectedIndex={transferServiceData?.findIndex(
                      (item) => item.id === selectedTransfer
                    )}
                    direction='column'
                    getLabel={(item) => (
                      <TransferItemForm
                        {...item}
                        count={count}
                        guestCount={initialValues.guestCount}
                        isRoundTrip={tripType === TransferType.ROUND_TRIP}
                      />
                    )}
                    getValue={(item) => item.id}
                    onChange={onChangeTransfer}
                    disabled={isSubmittingForm}
                  />
                </RadioGroupContainer>
              </TransferContainer>
            </FormContainer>
          </Content>
          {isDesktop ? (
            <Footer>
              <StyledButton
                variant='Primary'
                block={!isDesktop}
                onClick={() => mutate()}
                disabled={count <= 0 || !selectedTransfer}
                loading={isSubmittingForm}
              >
                Transfer Ekle
              </StyledButton>
            </Footer>
          ) : (
            <FooterMobile>
              <StyledButton
                variant='Primary'
                block={!isDesktop}
                onClick={() => mutate()}
                disabled={count <= 0 || !selectedTransfer}
                loading={isSubmittingForm}
              >
                Transfer Ekle
              </StyledButton>
            </FooterMobile>
          )}
        </>
      ) : (
        <div>
          <InfoNote variant='warning'>Transfer Bulunmamaktadır</InfoNote>
          <Footer>
            <StyledButton block={!isDesktop} onClick={onCancel}>
              Tamam
            </StyledButton>
          </Footer>
        </div>
      )}
    </Modal>
  );
};

export default TransferServiceForm;
